import { Trash2 } from 'lucide-react'
import { Button, Select } from '@/components/ui'
import type { Disposition, RmaLine } from '../api/types'
import { ALL_DISPOSITIONS, DISPOSITION_LABELS } from '../utils/stage-meta'

export interface RmaLineGridProps {
  lines: RmaLine[]
  disabled?: boolean
  onLineEdit: (lineId: number, edits: Partial<RmaLine>) => void
  onLineRemove: (lineId: number) => void
}

/**
 * Per-line review grid for the RMA detail drawer. Hand-rolled table
 * rather than the EditableGrid primitive — mirrors
 * `sales-inbox/components/SalesIntakeLineGrid.tsx`'s v1 approach:
 * RMA lines are few enough per return that inline inputs are simpler
 * than the full grid keyboard-navigation machinery.
 */
export function RmaLineGrid({ lines, disabled = false, onLineEdit, onLineRemove }: RmaLineGridProps) {
  return (
    <div className="overflow-x-auto rounded-sm border border-border-subtle bg-surface">
      <table className="min-w-full text-sm">
        <thead className="bg-surface-sunk text-role-table-header">
          <tr>
            <th className="px-2 py-2 text-left">#</th>
            <th className="px-2 py-2 text-left">Product</th>
            <th className="px-2 py-2 text-left">Lot #</th>
            <th className="px-2 py-2 text-right">Qty claimed</th>
            <th className="px-2 py-2 text-right">Qty received</th>
            <th className="px-2 py-2 text-left">UoM</th>
            <th className="px-2 py-2 text-left">Disposition</th>
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-t border-border-subtle text-role-table-cell">
              <td className="px-2 py-2 text-tertiary">{line.line_no}</td>
              <td className="px-2 py-2 text-secondary">
                <span className="block max-w-[24ch] truncate" title={line.product_name ?? ''}>
                  {line.product_name ?? <em className="text-tertiary">unset</em>}
                </span>
              </td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  defaultValue={line.lot_number ?? ''}
                  disabled={disabled}
                  onBlur={(e) => {
                    if (e.target.value !== (line.lot_number ?? '')) {
                      onLineEdit(line.id, { lot_number: e.target.value })
                    }
                  }}
                  className="w-24 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                  aria-label={`Lot number for line ${line.line_no}`}
                />
              </td>
              <td className="px-2 py-2 text-right">
                <input
                  type="number"
                  step="any"
                  defaultValue={line.quantity_claimed ?? ''}
                  disabled={disabled}
                  onBlur={(e) => {
                    const v = e.target.value
                    if (v !== (line.quantity_claimed ?? '')) {
                      onLineEdit(line.id, { quantity_claimed: v === '' ? null : v })
                    }
                  }}
                  className="w-20 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-right text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                  aria-label={`Quantity claimed for line ${line.line_no}`}
                />
              </td>
              <td className="px-2 py-2 text-right">
                <input
                  type="number"
                  step="any"
                  defaultValue={line.quantity_received ?? ''}
                  disabled={disabled}
                  onBlur={(e) => {
                    const v = e.target.value
                    if (v !== (line.quantity_received ?? '')) {
                      onLineEdit(line.id, { quantity_received: v === '' ? null : v })
                    }
                  }}
                  className="w-20 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-right text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                  aria-label={`Quantity received for line ${line.line_no}`}
                />
              </td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  defaultValue={line.uom ?? ''}
                  disabled={disabled}
                  onBlur={(e) => {
                    if (e.target.value !== (line.uom ?? '')) {
                      onLineEdit(line.id, { uom: e.target.value })
                    }
                  }}
                  className="w-16 rounded-xs border border-border-default bg-surface px-1.5 py-1 text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                  aria-label={`Unit of measure for line ${line.line_no}`}
                />
              </td>
              <td className="px-2 py-2">
                <Select
                  value={line.disposition}
                  disabled={disabled}
                  onChange={(e) =>
                    onLineEdit(line.id, { disposition: e.target.value as Disposition })
                  }
                >
                  {ALL_DISPOSITIONS.map((d) => (
                    <option key={d} value={d}>
                      {DISPOSITION_LABELS[d]}
                    </option>
                  ))}
                </Select>
              </td>
              <td className="px-2 py-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  disabled={disabled}
                  aria-label={`Remove line ${line.line_no}`}
                  onClick={() => onLineRemove(line.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
